/**
 * Upload size constants — single source of truth for file upload limits.
 *
 * Mirrored backend-side in `functions/src/lib/uploadLimits.ts`. When changing
 * values here, update the backend mirror and `storage.rules` in the same pass.
 *
 * Raised to 25MB/100MB on 2026-05-06 (was 10MB per file).
 * Raised to 50MB/250MB on 2026-06-06 to accommodate large scanned fax batches
 * (9+ PDFs at 15-30 MB each, 2026-06-05 Fernando test: 9 faxes failed Layer 1).
*
 * PER-FILE RAISED TO 300MB on 2026-09-14, and the two numbers now mean
 * genuinely different things — the second is NOT "the first times a few files".
 *
 * PER-FILE is bounded by TIME, and no longer by memory. Indexing reads the
 * document from storage a window at a time, so peak memory is a property of
 * the window rather than the file. What one invocation can finish is
 * 2.71 s/MB (measured live 2026-09-14: 45 MB in 121.9 s) against
 * prest0nProcessIngestJob's 540 s, the platform maximum for an event-driven
 * trigger and unraisable. That alone caps a file near 166 MB. 300MB is
 * deliberately PAST it: such a document pauses at its budget with a
 * checkpoint and a fresh invocation finishes it. Consequence worth knowing:
 * a file over ~88MB is not fully indexed by the FIRST turn after upload (the
 * turn waits 120 s, extended to 240 s). It says so honestly, and a later turn
 * has the whole document.
 *
 * TOTAL is bounded by MEMORY and has NOT moved. It guards
 * attachmentDocumentAnalysis, which downloads every attachment into
 * `prepared[]` before analysing any of them inside a 2 GiB instance — "a
 * guard on a memory shape, wearing the costume of a product limit", in its
 * own words. The INGEST lane holds no bytes at all (GCS paths to the
 * extractor, byte ranges for indexing), which is why per-file can move well
 * ahead of total. Raising TOTAL needs that lane changed first, not a bigger
 * number here.
 */

/** Maximum raw bytes per uploaded file (300 MB). */
export const MAX_UPLOAD_BYTES_PER_FILE = 300 * 1024 * 1024;

/** Maximum aggregate raw bytes across all attachments in a single request (250 MB). */
export const MAX_UPLOAD_BYTES_TOTAL = 250 * 1024 * 1024;

/** Human-readable label used in UI messages (no trailing period). */
export const MAX_UPLOAD_LABEL_PER_FILE = '300MB';
export const MAX_UPLOAD_LABEL_TOTAL = '250MB';
